import { v3 } from '@greensm/api-client';
import { useEventStream, type StreamStatus } from '@greensm/sse';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { qk } from '@/shared/lib/queryKeys';
import { useServices } from './services';

type Listener = (e: v3.V3Event) => void;
interface Ctx { status: v3.V3ConnectionStatus; subscribe: (l: Listener) => () => void }
const StreamCtx = createContext<Ctx | null>(null);

const toConnection = (s: StreamStatus): v3.V3ConnectionStatus =>
  s === 'open' ? 'connected' : s === 'closed' ? 'offline' : 'reconnecting';

/**
 * MỘT kết nối SSE v3 cho cả app (05 FE-1.3 mục 5 – áp dụng chung cho app khách).
 * - trip.updated / flood.updated ⇒ cập nhật thẳng cache để màn đổi trong ≤ 2 s, không tải lại trang.
 * - resync hoặc vừa kết nối lại ⇒ tải lại toàn bộ dữ liệu v3 đang mở (05 §2 quy tắc 4).
 * - Màn cần sự kiện khác (message.created…) dùng useV3Event.
 */
export function V3StreamProvider({ children, enabled = true }: { children: ReactNode; enabled?: boolean }) {
  const { v3StreamUrl, getV3Token, v3FetchImpl } = useServices();
  const qc = useQueryClient();
  const listeners = useRef(new Set<Listener>());

  const onMessage = useMemo(() => {
    const dispatch = (e: v3.V3Event) => listeners.current.forEach((l) => l(e));
    const route = v3.createV3EventRouter({
      'trip.updated': ({ trip }) => {
        const previous = qc.getQueryData<v3.TripView>(qk.v3.trip(trip.trip_id));
        if (previous && previous.trip_version > trip.trip_version) return;
        void qc.cancelQueries({ queryKey: qk.v3.trip(trip.trip_id) });
        void qc.cancelQueries({ queryKey: qk.v3.currentTrip });
        qc.setQueryData(qk.v3.trip(trip.trip_id), trip);
        qc.setQueryData<v3.TripView | null>(qk.v3.currentTrip, (cur) =>
          cur === undefined || cur?.trip_id === trip.trip_id || cur === null ? (['COMPLETED', 'CANCELLED'].includes(trip.stage) ? null : trip) : cur);
        // Refresh history for business changes, without fetching it on every GPS tick.
        if (!previous || previous.trip_version !== trip.trip_version || previous.stage !== trip.stage) {
          void qc.invalidateQueries({ queryKey: ['v3', 'history'] });
          void qc.invalidateQueries({ queryKey: qk.v3.historyTrip(trip.trip_id) });
          void qc.invalidateQueries({ queryKey: ['notifications'] });
        }
      },
      'flood.updated': ({ flood }) => {
        qc.setQueryData<v3.FloodView[]>(qk.v3.floods, (cur) => {
          if (!cur) return cur;
          const rest = cur.filter((f) => f.incident_id !== flood.incident_id);
          // Khách chỉ thấy điểm VERIFIED; CLEARED / EXPIRED ⇒ bỏ khỏi bản đồ.
          return flood.status === 'VERIFIED' ? [...rest, flood] : rest;
        });
      },
      onResync: () => { void qc.invalidateQueries({ queryKey: qk.v3.all }); void qc.invalidateQueries({ queryKey: ['notifications'] }); },
    });
    return (m: { event: string; data: string; id?: string }) => {
      route(m);
      const e = v3.parseV3Event(m);
      if (e) dispatch(e);
    };
  }, [qc]);

  const raw = useEventStream({
    url: v3StreamUrl,
    getToken: getV3Token,
    fetchImpl: v3FetchImpl,
    delayFor: v3.v3BackoffDelay,
    idleTimeoutMs: v3.V3_IDLE_TIMEOUT_MS,
    onMessage,
    onReconnected: () => {
      void qc.invalidateQueries({ queryKey: qk.v3.all });
      // Dữ liệu ngoài react-query (hội thoại của bộ trợ lý) cũng phải tải lại như khi nhận `resync`.
      listeners.current.forEach((l) => l({ type: 'resync', data: { reason: 'reconnected' } } as v3.V3Event));
    },
  }, enabled);

  useEffect(() => {
    if (raw === 'open') void qc.invalidateQueries({ queryKey: qk.v3.all });
  }, [raw, qc]);

  // subscribe giữ nguyên danh tính qua các lần đổi trạng thái kết nối ⇒ nơi nghe không phải đăng ký lại.
  const subscribe = useCallback((l: Listener) => { listeners.current.add(l); return () => { listeners.current.delete(l); }; }, []);
  const value = useMemo<Ctx>(() => ({ status: toConnection(raw), subscribe }), [raw, subscribe]);

  return <StreamCtx.Provider value={value}>{children}</StreamCtx.Provider>;
}

/** Trạng thái kết nối SSE v3 – hiển thị chấm/dải "Mất kết nối". */
export function useV3Status(): v3.V3ConnectionStatus {
  return useContext(StreamCtx)?.status ?? 'offline';
}

/** Đăng ký nghe mọi sự kiện v3 (adapter cần nhiều loại, vd. bộ trợ lý). null khi nằm ngoài V3StreamProvider. */
export function useV3Subscribe(): Ctx['subscribe'] | null {
  return useContext(StreamCtx)?.subscribe ?? null;
}

/** Nghe một loại sự kiện v3. Handler mới nhất luôn được gọi (không cần useCallback). */
export function useV3Event<T extends v3.V3EventType>(type: T, handler: (data: Extract<v3.V3Event, { type: T }>['data']) => void): void {
  const ctx = useContext(StreamCtx);
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!ctx) return;
    return ctx.subscribe((e) => { if (e.type === type) ref.current(e.data as never); });
  }, [ctx, type]);
}
